/**
 * New Ticket Screen — RN creates a new chat conversation.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useState } from "react";
import {
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";

import { colors, fonts, radius, shadows, spacing, typography } from "@/lib/theme";
import { createRnTicket, sendTicketMessage } from "@/lib/ticket-api";

export default function NewTicketScreen() {
    const router = useRouter();
    const { prefill, visitId } = useLocalSearchParams<{ prefill?: string; visitId?: string }>();
    const insets = useSafeAreaInsets();
    const [subject, setSubject] = useState(prefill ?? "");
    const [creating, setCreating] = useState(false);

    const handleCreate = async () => {
        if (creating) {
            return;
        }
        setCreating(true);

        try {
            // Create the ticket
            const result = await createRnTicket({
                channel: "in_app_chat",
                visitId: visitId ? Number(visitId) : undefined
            });

            // Send the text as the first actual message (not just a subject)
            const text = subject.trim();
            if (text) {
                await sendTicketMessage(result.data.id, text);
            }

            // Navigate to the new ticket's chat
            router.replace(`/tickets/${result.data.id}`);
        } catch {
            setCreating(false);
        }
    };

    return (
        <SafeAreaView style={styles.safe} edges={["top", "bottom"]}>
            <Stack.Screen options={{ headerShown: false }} />
            <KeyboardAvoidingView
                style={styles.flex}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
                keyboardVerticalOffset={Platform.OS === "ios" ? 12 : 0}
            >
                <View style={styles.header}>
                    <TouchableOpacity onPress={() => router.back()} activeOpacity={0.7}>
                        <MaterialCommunityIcons
                            name="arrow-left"
                            size={24}
                            color={colors.textPrimary}
                        />
                    </TouchableOpacity>
                    <Text style={typography.subtitle}>New Conversation</Text>
                    <View style={{ width: 24 }} />
                </View>

                <ScrollView
                    contentContainerStyle={styles.scrollContent}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "none"}
                    automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                >
                    <View style={styles.content}>
                        <Text style={styles.label}>What do you need help with?</Text>
                        <TextInput
                            style={styles.input}
                            placeholder="Type your message here..."
                            placeholderTextColor={colors.textTertiary}
                            value={subject}
                            onChangeText={setSubject}
                            multiline
                            maxLength={2000}
                            autoFocus
                        />
                    </View>

                    <View
                        style={[
                            styles.footer,
                            { paddingBottom: Math.max(insets.bottom, spacing.lg) }
                        ]}
                    >
                        <TouchableOpacity
                            style={[styles.createBtn, creating && styles.createBtnDisabled]}
                            onPress={handleCreate}
                            activeOpacity={0.7}
                            disabled={creating}
                        >
                            {creating ? (
                                <ActivityIndicator size="small" color={colors.surface} />
                            ) : (
                                <>
                                    <MaterialCommunityIcons
                                        name="chat-plus-outline"
                                        size={20}
                                        color={colors.surface}
                                    />
                                    <Text style={styles.createBtnText}>Start Chat</Text>
                                </>
                            )}
                        </TouchableOpacity>
                    </View>
                </ScrollView>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.background },
    flex: { flex: 1 },
    scrollContent: {
        flexGrow: 1
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.md
    },
    content: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.xl
    },
    label: {
        fontFamily: fonts.semiBold,
        fontSize: 16,
        color: colors.textPrimary,
        marginBottom: spacing.md
    },
    input: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        padding: spacing.base,
        fontFamily: fonts.regular,
        fontSize: 15,
        color: colors.textPrimary,
        minHeight: 100,
        textAlignVertical: "top",
        ...shadows.xs
    },
    hint: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textTertiary,
        marginTop: spacing.sm
    },
    footer: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.xl,
        marginTop: "auto"
    },
    createBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.teal500,
        borderRadius: radius.lg,
        paddingVertical: spacing.md,
        gap: spacing.sm,
        ...shadows.sm
    },
    createBtnDisabled: { opacity: 0.6 },
    createBtnText: {
        fontFamily: fonts.semiBold,
        fontSize: 16,
        color: colors.surface
    }
});
